import React, { useState, useEffect, useCallback } from 'react';
import { apiProductos } from '../../services/Axios';
import { useAuth } from '../../services/AuthContext';

interface QuickEditProduct {
  _id: string;
  nombre: string;
  unidadMedida: "unidad" | "kg" | "gramo" | "litro" | "metro";
  cantidad: number;
  valorCompra: number;
  margenGanancia: number;
  valorVentaTotal: number;
  ivaPorcentaje?: number;
  tipoCalculo?: 'recargo' | 'margen';
  esVarios?: boolean;
}

interface QuickEditProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: QuickEditProduct | null;
  onSaveSuccess: () => void;
}

const QuickEditProductModal: React.FC<QuickEditProductModalProps> = ({
  isOpen,
  onClose,
  product,
  onSaveSuccess,
}) => {
  const { userData } = useAuth();
  const [nombre, setNombre] = useState('');
  const [unidadMedida, setUnidadMedida] = useState<'unidad' | 'kg' | 'gramo' | 'litro' | 'metro'>('unidad');
  const [cantidad, setCantidad] = useState('');
  const [valorCompra, setValorCompra] = useState('');
  const [margenGanancia, setMargenGanancia] = useState('');
  const [valorVentaTotal, setValorVentaTotal] = useState('');
  const [tipoCalculo, setTipoCalculo] = useState<'recargo' | 'margen'>('recargo');
  const [isSaving, setIsSaving] = useState(false);
  
  // Errores individuales por campo
  const [errors, setErrors] = useState({
    nombre: '',
    cantidad: '',
    valorCompra: '',
    margenGanancia: '',
  });

  // Cargar datos del producto al abrir el modal
  useEffect(() => {
    if (isOpen && product) {
      setNombre(product.nombre);
      setUnidadMedida(product.unidadMedida as any);
      setCantidad(product.cantidad.toString());
      setValorCompra(product.valorCompra.toString());
      setMargenGanancia(product.margenGanancia.toString());
      setValorVentaTotal(product.valorVentaTotal.toString());
      setTipoCalculo(product.tipoCalculo ?? 'recargo');
      setErrors({ nombre: '', cantidad: '', valorCompra: '', margenGanancia: '' });
    }
  }, [isOpen, product]);

  // Calcular valor venta total automáticamente
  const calcularValorVentaTotal = useCallback(() => {
    if (valorCompra && margenGanancia) {
      const valorCompraNum = parseFloat(valorCompra);
      const margenNum = parseFloat(margenGanancia);
      const ivaPorcentaje = product?.ivaPorcentaje || 0;

      if (!isNaN(valorCompraNum) && !isNaN(margenNum) && valorCompraNum > 0) {
        const valorVenta = userData?.organization?.responsableIVA
          ? valorCompraNum * (1 + ivaPorcentaje / 100)
          : valorCompraNum;

        let valorVentaTotalCalculado: number;
        if (tipoCalculo === 'margen') {
          if (margenNum >= 100) { setValorVentaTotal(''); return; }
          valorVentaTotalCalculado = valorVenta / (1 - margenNum / 100);
        } else {
          valorVentaTotalCalculado = valorVenta * (1 + margenNum / 100);
        }
        setValorVentaTotal(Math.round(valorVentaTotalCalculado).toString());
      }
    }
  }, [valorCompra, margenGanancia, tipoCalculo, product?.ivaPorcentaje, userData?.organization?.responsableIVA]);

  useEffect(() => {
    calcularValorVentaTotal();
  }, [calcularValorVentaTotal]);

  // Envolver handleSave en useCallback
    const handleSave = useCallback(async () => {
    // Limpiar errores previos
    const newErrors = {
        nombre: '',
        cantidad: '',
        valorCompra: '',
        margenGanancia: '',
    };

    // Validaciones
    if (!nombre.trim()) {
        newErrors.nombre = 'El nombre es requerido';
    }
    if (!cantidad || parseFloat(cantidad) <= 0) {
        newErrors.cantidad = 'La cantidad debe ser mayor a 0';
    }
    if (!valorCompra || parseFloat(valorCompra) <= 0) {
        newErrors.valorCompra = 'El valor de compra debe ser mayor a 0';
    }
    if (!margenGanancia || parseFloat(margenGanancia) < 0) {
        newErrors.margenGanancia = 'El margen de ganancia no puede ser negativo';
    } else if (tipoCalculo === 'margen' && parseFloat(margenGanancia) >= 100) {
        newErrors.margenGanancia = 'Con margen sobre venta el porcentaje debe ser menor a 100%';
    }

    // Si hay errores, mostrarlos y no continuar
    if (newErrors.nombre || newErrors.cantidad || newErrors.valorCompra || newErrors.margenGanancia) {
        setErrors(newErrors);
        return;
    }

    setIsSaving(true);
    setErrors({ nombre: '', cantidad: '', valorCompra: '', margenGanancia: '' });

    try {
        // Preparar datos para enviar (solo los campos editables)
        const updateData = {
            nombre: nombre.trim(),
            unidadMedida,
            cantidad: parseFloat(cantidad),
            valorCompra: parseFloat(valorCompra),
            margenGanancia: parseFloat(margenGanancia),
            tipoCalculo,
            valorVentaTotal: parseFloat(valorVentaTotal),
        };

        await apiProductos.put(`/api/productos/${product?._id}`, updateData);
        
        onSaveSuccess();
        onClose();
    } catch (err: any) {
        // Error general del servidor
        setErrors({
        nombre: err.response?.data?.msg || 'Error al actualizar el producto',
        cantidad: '',
        valorCompra: '',
        margenGanancia: '',
        });
    } finally {
        setIsSaving(false);
    }
    }, [nombre, unidadMedida, cantidad, valorCompra, margenGanancia, tipoCalculo, valorVentaTotal, product?._id, onSaveSuccess, onClose]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
        if (!isOpen) return;

        // ESC para cerrar
        if (e.key === 'Escape') {
            onClose();
        }
        // SHIFT para guardar (solo SHIFT, sin otras teclas modificadoras)
        else if (e.shiftKey && !e.ctrlKey && !e.altKey && !e.metaKey && !isSaving) {
            e.preventDefault();
            // Validar que los campos estén completos antes de guardar
            if (nombre.trim() && cantidad && valorCompra && margenGanancia &&
                parseFloat(cantidad) > 0 && parseFloat(valorCompra) > 0 && parseFloat(margenGanancia) >= 0) {
            handleSave();
            }
        }
        };

        if (isOpen) {
        window.addEventListener('keydown', handleKeyDown);
        }

        return () => {
        window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, isSaving, nombre, cantidad, valorCompra, margenGanancia, onClose, handleSave]);

  if (!isOpen || !product) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 sticky top-0 z-10">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <i className="fas fa-edit text-white text-lg"></i>
              </div>
              <h2 className="text-xl font-bold text-white">Edición Rápida</h2>
            </div>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors"
              disabled={isSaving}
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Nombre - Ocupa toda la fila */}
            <div className="md:col-span-3">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-tag mr-2 text-blue-600"></i>
                Nombre del producto <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={nombre}
                onChange={(e) => {
                  setNombre(e.target.value);
                  if (errors.nombre) setErrors({ ...errors, nombre: '' });
                }}
                className={`w-full px-4 py-3 border ${errors.nombre ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all`}
                disabled={isSaving}
              />
              {errors.nombre && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.nombre}
                </p>
              )}
            </div>

            {/* Valor Compra */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-dollar-sign mr-2 text-blue-600"></i>
                Valor Compra <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  type="text"
                  value={valorCompra}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    setValorCompra(val);
                    if (errors.valorCompra) setErrors({ ...errors, valorCompra: '' });
                  }}
                  placeholder="0"
                  className={`w-full pl-8 pr-4 py-3 border ${errors.valorCompra ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-right font-medium`}
                  disabled={isSaving}
                />
              </div>
              {errors.valorCompra && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.valorCompra}
                </p>
              )}
            </div>

            {/* Tipo de Cálculo */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-calculator mr-2 text-blue-600"></i>
                Tipo de Cálculo
                <span
                  className="ml-1 text-gray-400 cursor-pointer"
                  title="Recargo sobre costo: le sumas un % al costo. Ej: $1.000 + 30% = $1.300. | Margen sobre venta: el % es tu ganancia sobre el precio final. Ej: margen 30% sobre $1.000 = $1.429. Si no sabes cuál usar, elige Recargo sobre costo."
                >ⓘ</span>
              </label>
              <select
                value={tipoCalculo}
                onChange={(e) => setTipoCalculo(e.target.value as 'recargo' | 'margen')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                disabled={isSaving}
              >
                <option value="recargo">Recargo sobre Costo</option>
                <option value="margen">Margen sobre Venta</option>
              </select>
            </div>

            {/* Margen de Ganancia */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-percent mr-2 text-blue-600"></i>
                {tipoCalculo === 'margen' ? 'Margen sobre Venta (%)' : 'Margen de Ganancia (%)'} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={margenGanancia}
                  onChange={(e) => {
                    const val = e.target.value.replace(/[^0-9.]/g, '').replace(/(\..*?)\..*/g, '$1');
                    setMargenGanancia(val);
                    if (errors.margenGanancia) setErrors({ ...errors, margenGanancia: '' });
                  }}
                  placeholder="0"
                  className={`w-full pl-4 pr-8 py-3 border ${errors.margenGanancia ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all text-right font-medium`}
                  disabled={isSaving}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">%</span>
              </div>
              {errors.margenGanancia && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.margenGanancia}
                </p>
              )}
            </div>

            {/* Valor Venta Total (calculado) */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-cash-register mr-2 text-blue-600"></i>
                Valor Venta Total
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  type="text"
                  value={parseInt(valorVentaTotal || '0').toLocaleString()}
                  readOnly
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 bg-gray-50 rounded-lg text-right font-bold text-blue-700"
                />
              </div>
            </div>

            {/* Unidad de Medida */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-balance-scale mr-2 text-blue-600"></i>
                Unidad de Medida <span className="text-red-500">*</span>
              </label>
              <select
                value={unidadMedida}
                onChange={(e) => setUnidadMedida(e.target.value as any)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                disabled={isSaving}
              >
                <option value="unidad">Unidad</option>
                <option value="kg">Kilogramo (Kg)</option>
                <option value="gramo">Gramo (g)</option>
                <option value="litro">Litro (L)</option>
                <option value="metro">Metro (m)</option>
              </select>
            </div>

            {/* Cantidad */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-boxes mr-2 text-blue-600"></i>
                Cantidad <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={cantidad}
                onChange={(e) => {
                  let val = e.target.value.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
                  if (unidadMedida === 'unidad') {
                    val = val.replace(/\..*/, '');
                  } else {
                    val = val.replace(/(\..*?)\..*/g, '$1');
                  }
                  setCantidad(val);
                  if (errors.cantidad) setErrors({ ...errors, cantidad: '' });
                }}
                placeholder="0"
                className={`w-full px-4 py-3 border ${errors.cantidad ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all`}
                disabled={isSaving}
              />
              {errors.cantidad && (
                <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                  <i className="fas fa-exclamation-circle"></i>
                  {errors.cantidad}
                </p>
              )}
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-2">
            <i className="fas fa-info-circle mr-1"></i>
            El valor de venta se calcula automáticamente
          </p>

          {/* Footer Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fas fa-save"></i>
                  Guardar Cambios
                </>
              )}
            </button>
          </div>
        </div>

        {/* Guía de atajos de teclado */}
        <div className="px-6 py-3 bg-blue-50 border-t border-blue-200 flex justify-center gap-6">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded">ESC</span>
            <span className="text-sm text-blue-700">Cancelar</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-blue-600 text-white text-xs font-bold rounded">SHIFT</span>
            <span className="text-sm text-blue-700">Guardar</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickEditProductModal;